import { BaseComponent } from '../BaseComponent';
import { DataClassUtil } from '../../utils/DataClassUtil';
import { BreakpointUtil } from '../../utils/BreakpointUtil';

class Navigation extends BaseComponent {
  private readonly menuItems: NodeListOf<HTMLElement>;
  private readonly backButtons: NodeListOf<HTMLElement>;
  private readonly itemWithSubmenu: NodeListOf<HTMLElement>;
  public readonly toggle: HTMLElement | null;

  private open = false;
  private currentlyOpenColumn = 0;
  private currentlyOpenSubmenu: number | null = 0;
  private targetColumn: number | null = null;
  private targetSubmenu: number | null = null;

  constructor(rootElement: HTMLElement) {
    super(rootElement);

    this.toggle = rootElement.querySelector('.js-navigation-button');
    this.menuItems = rootElement.querySelectorAll('.js-navigation-item');
    this.backButtons = rootElement.querySelectorAll('.js-navigation-backbutton');
    this.itemWithSubmenu = rootElement.querySelectorAll('.js-navigation-item-with-submenu');
  }

  private handleClick = () => {
    this.open = !this.open;
    this.stateHandler();
  };

  private stateHandler = () => {
    if (this.open) {
      DataClassUtil.add('openClass', this.rootElement);
      DataClassUtil.add('openNavigation', document.body);
      this.updateFoldoutHeight();
    } else {
      DataClassUtil.remove('openClass', this.rootElement);
      DataClassUtil.remove('openNavigation', document.body);
      this.closeColumn();
    }
  };

  private updateFoldoutHeight = () => {
    const targetColumnID = this.targetColumn || 0;
    const foldOutElement = document.querySelector<HTMLElement>(`.js-navigation-foldout-content`);
    const selector = `.js-navigation-col-${targetColumnID} > div:not(.hidden), .js-navigation-col-${targetColumnID} > ul`;
    const foldOutContents = document.querySelector<HTMLElement>(selector);

    if (!foldOutElement || !foldOutContents) return;

    const heightToSet = foldOutContents.getBoundingClientRect().height;
    if (heightToSet) {
      foldOutElement.style.minHeight = `${heightToSet + 200}px`;
    }
  };

  private openColumn = () => {
    if (!this.targetColumn) return;

    const colElementToOpen = document.querySelector<HTMLElement>(`.js-navigation-col-${this.targetColumn}`);
    if (!colElementToOpen) return;

    colElementToOpen.classList.add('open');
    this.currentlyOpenColumn = this.targetColumn;
    this.rootElement.dataset.currentlyOpenColumn = this.currentlyOpenColumn.toString();

    this.targetColumn = null;
  };

  private closeColumn = (menuItem?: HTMLElement) => {
    if (this.currentlyOpenColumn === 0) return;

    const columnElementToClose = document.querySelector(`.js-navigation-col-${this.currentlyOpenColumn}`);
    if (!columnElementToClose) return;

    this.rootElement.dataset.currentlyOpenColumn = (this.currentlyOpenColumn - 1).toString();

    columnElementToClose.addEventListener('transitionend', this.afterClosingTransition);
    columnElementToClose.classList.remove('open', 'active');
    this.updateFoldoutHeight();

    menuItem?.classList.remove('active');
  };

  private afterClosingTransition = () => {
    const currentColumn = document.querySelector(`.js-navigation-col-${this.currentlyOpenColumn}`);
    if (currentColumn) {
      currentColumn.removeEventListener('transitionend', this.afterClosingTransition);

      // hide submenu list in closed column
      const submenuToClose = currentColumn.querySelector(`.js-navigation-submenu:not(.hidden)`);
      if (submenuToClose) {
        submenuToClose.classList.add('hidden');
      }
      this.currentlyOpenColumn--;

      // close another column if containing submenu list needs to be swapped or no target column is defined
      if (
        (!this.targetColumn && this.currentlyOpenColumn > 0) ||
        (this.targetColumn == this.currentlyOpenColumn &&
          this.targetSubmenu != null &&
          this.currentlyOpenSubmenu != this.targetSubmenu)
      ) {
        this.closeColumn();
      } else {
        // open new column with correct submenu list
        if (this.targetColumn && this.currentlyOpenColumn < this.targetColumn) {
          this.openSubmenu();
          this.openColumn();
        }
      }
    }
  };

  private openSubmenu = () => {
    const colElement = document.querySelector(`.js-navigation-col-${this.targetColumn}`);
    if (colElement) {
      const submenuToOpen = colElement.querySelector(`.js-navigation-submenu-${this.targetSubmenu}`);

      if (submenuToOpen) {
        this.currentlyOpenSubmenu = this.targetSubmenu;
        submenuToOpen.classList.remove('hidden');
        this.updateFoldoutHeight();
      }

      // reset target
      this.targetSubmenu = null;
    }
  };

  private findAncestor = (el: HTMLElement, cls: string): HTMLElement | null => {
    let currentEl: HTMLElement | null = el;
    while (currentEl && currentEl.parentElement) {
      currentEl = currentEl.parentElement;
      if (currentEl.classList.contains(cls)) {
        return currentEl;
      }
    }
    return null;
  };

  private handleMenuItem = (e: Event) => {
    e.preventDefault();
    const interactedElement = e.currentTarget as HTMLElement;
    if (!interactedElement) return;

    this.targetColumn = interactedElement.dataset.targetCol ? parseInt(interactedElement.dataset.targetCol) : null;
    this.targetSubmenu = interactedElement.dataset.targetSubmenu ? parseInt(interactedElement.dataset.targetSubmenu) : null;

    if (this.targetColumn === this.currentlyOpenColumn && this.targetSubmenu === this.currentlyOpenSubmenu) {
      return;
    }

    this.clearActiveState(interactedElement);
    interactedElement.classList.add('active');
    this.handleNavigation(interactedElement, e);
  };

  private clearActiveState(interactedElement: HTMLElement) {
    const allColumns = Array.from(this.rootElement.querySelectorAll<HTMLElement>('.navigation__menu-col'));
    const parentColumn = this.findAncestor(interactedElement, 'navigation__menu-col');

    if (!allColumns || !parentColumn) return;

    const parentColumnIndex = allColumns.indexOf(parentColumn);
    if (parentColumnIndex === -1) return;

    for (let i = parentColumnIndex; i < allColumns.length; i++) {
      this.rootElement.querySelectorAll(`.js-navigation-col-${i} .js-navigation-item.active`)
        .forEach(item => item.classList.remove('active'));
    }
  }

  private handleNavigation(element: HTMLElement, e: Event) {
    // Find the column this navigation item belongs to
    const parentColumn = this.findAncestor(element, 'navigation__menu-col');
    const allColumns = Array.from(this.rootElement.querySelectorAll<HTMLElement>('.navigation__menu-col'));
    const parentColumnIndex = parentColumn && allColumns ? allColumns.indexOf(parentColumn) : undefined;

    // Simple link with no target column/submenu specified
    if (!this.targetColumn && !this.targetSubmenu && parentColumnIndex !== undefined) {
      if (e.type === 'click') {
        this.followLink(element);
      } else if (this.currentlyOpenColumn !== parentColumnIndex) {
        this.targetColumn = parentColumnIndex;
        this.closeColumn();
      }
      return;
    }

    // Target is current column or a previous column - close current and go back
    if (this.targetColumn &&
        (this.targetColumn === this.currentlyOpenColumn || this.targetColumn < this.currentlyOpenColumn)) {
      this.closeColumn(element);
      return;
    }

    // Open a new submenu in a new column
    if (this.targetColumn && this.targetSubmenu) {
      this.openSubmenu();
      this.openColumn();
    }
  }

  private followLink(element: HTMLElement) {
    const link = element.getAttribute('href');
    const target = element.getAttribute('target');

    if (link && target === '_self') {
      window.location.href = link;
    } else if (link && target === '_blank') {
      window.open(link);
    }
  }

  private handleBackbutton = () => {
    this.targetColumn = this.currentlyOpenColumn - 1;
    this.closeColumn();
  };

  private handleKeyboardNavigation = (e: KeyboardEvent, mainItem: HTMLElement) => {
    const foldout = mainItem.nextElementSibling as HTMLElement;
    if (!foldout) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleFoldout(foldout, mainItem, e);
    }
    else if (e.key === 'Tab') {
      const isOpen = mainItem.getAttribute('aria-expanded') === 'true';
      if (!isOpen) return;

      if (e.shiftKey && document.activeElement === mainItem) {
        this.closeFoldout(foldout, mainItem);
        return;
      }

      const focusableElements = this.getFocusableElements(foldout);
      if (focusableElements.length === 0) return;

      const isLastElement = document.activeElement === focusableElements[focusableElements.length - 1];
      const isFirstElement = document.activeElement === focusableElements[0];

      if ((!e.shiftKey && isLastElement) || (e.shiftKey && isFirstElement)) {
        this.closeFoldout(foldout, mainItem);

        if (e.shiftKey && isFirstElement) {
          setTimeout(() => mainItem.focus(), 0);
        }
      }
    }
  };

  private addFoldoutTabListeners() {
    if (!this.itemWithSubmenu) return;

    this.itemWithSubmenu.forEach((mainItem) => {
      const foldout = mainItem.nextElementSibling as HTMLElement;
      if (!foldout) return;

      // Handle tabbing in/out of foldout menus
      foldout.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        const focusableElements = this.getFocusableElements(foldout);
        if (focusableElements.length === 0) return;

        const isForwardTab = !e.shiftKey;
        const isLastElement = document.activeElement === focusableElements[focusableElements.length - 1];
        const isFirstElement = document.activeElement === focusableElements[0];

        // Close the foldout when tabbing beyond its first/last elements
        if ((isForwardTab && isLastElement) || (!isForwardTab && isFirstElement)) {
          setTimeout(() => {
            this.closeFoldout(foldout, mainItem);

            // When tabbing backwards from first element, focus the parent menu item
            if (!isForwardTab && isFirstElement) {
              mainItem.focus();
            }
          }, 0);
        }
      });
    });
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex="0"]'));
  }

  private setFoldoutFocusable(foldout: HTMLElement, focusable: boolean): void {
    const elements = this.getFocusableElements(foldout);
    elements.forEach(el => {
      if (focusable) {
        // If element had a stored tabindex, restore it, otherwise remove the negative tabindex
        const storedTabindex = el.dataset.originalTabindex;
        if (storedTabindex) {
          if (storedTabindex === 'null') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', storedTabindex);
          }
          delete el.dataset.originalTabindex;
        } else {
          el.removeAttribute('tabindex');
        }
      } else {
        // Store original tabindex if it exists
        if (el.hasAttribute('tabindex')) {
          el.dataset.originalTabindex = el.getAttribute('tabindex') || 'null';
        } else {
          el.dataset.originalTabindex = 'null';
        }
        // Set to -1 to make it unfocusable by keyboard
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  private toggleFoldout(foldout: HTMLElement, mainItem: HTMLElement, e: Event): void {
    const isOpen = mainItem.getAttribute('aria-expanded') === 'true';

    this.handleMenuItem(e);

    if (isOpen) {
      const target = e.currentTarget as HTMLElement;
      target?.classList.remove('active');
      mainItem.setAttribute('aria-expanded', 'false');
      this.setFoldoutFocusable(foldout, false);
      this.updateFoldoutHeight();
    } else {
      mainItem.setAttribute('aria-expanded', 'true');
      this.setFoldoutFocusable(foldout, true);
      this.updateFoldoutHeight();
    }
  }

  private closeFoldout(foldout: HTMLElement, mainItem: HTMLElement): void {
    if (mainItem.getAttribute('aria-expanded') === 'true') {
      mainItem.setAttribute('aria-expanded', 'false');
      mainItem.classList.remove('active');
      this.setFoldoutFocusable(foldout, false);
    }
  }

  private setupHoverBehavior(): void {
    // Only apply hover behavior on desktop
    if (!this.itemWithSubmenu || !BreakpointUtil.md) return;

    this.itemWithSubmenu.forEach(mainItem => {
      const foldout = mainItem.nextElementSibling as HTMLElement;
      if (!foldout) return;

      // Open menu on hover
      mainItem.addEventListener('mouseenter', () => {
        if (mainItem.getAttribute('aria-expanded') !== 'true') {
          mainItem.setAttribute('aria-expanded', 'true');
          mainItem.classList.add('active');
          this.setFoldoutFocusable(foldout, true);
          this.updateFoldoutHeight();
        }
      });

      // Handle mouse movements between menu item and its foldout
      const handleMouseLeave = (e: MouseEvent) => {
        const toElement = e.relatedTarget as HTMLElement;

        // Don't close if moving between menu item and its foldout
        if (foldout.contains(toElement) || mainItem.contains(toElement)) {
          return;
        }

        this.closeFoldout(foldout, mainItem);
      };

      // Apply the same handler to both elements
      mainItem.addEventListener('mouseleave', handleMouseLeave);
      foldout.addEventListener('mouseleave', handleMouseLeave);
    });
  }

  public init() {
    // Main navigation toggle
    this.toggle?.addEventListener('click', this.handleClick);

    // Setup submenu items and keyboard navigation
    this.initFoldoutMenus();

    // Setup main menu items with appropriate event based on device
    this.initMenuItems();

    // Back button handlers
    this.backButtons?.forEach(backButton => {
      backButton.addEventListener('click', this.handleBackbutton);
    });

    // Setup hover behavior (only applies on desktop)
    this.setupHoverBehavior();
  }

  private initFoldoutMenus(): void {
    if (!this.itemWithSubmenu) return;

    this.itemWithSubmenu.forEach(mainItem => {
      const foldout = mainItem.nextElementSibling as HTMLElement;
      if (!foldout) return;

      // Initialize state
      this.setFoldoutFocusable(foldout, false);
      mainItem.setAttribute('aria-expanded', 'false');

      // Add keyboard navigation
      mainItem.addEventListener('keydown', (e: KeyboardEvent) => this.handleKeyboardNavigation(e, mainItem));
    });

    // Add tab navigation for foldout menus
    this.addFoldoutTabListeners();
  }

  private initMenuItems(): void {
    if (!this.menuItems) return;

    // Use mouseenter on desktop, click on mobile
    const eventType = BreakpointUtil.md ? 'mouseenter' : 'click';
    this.menuItems.forEach(menuItem => {
      menuItem.addEventListener(eventType, this.handleMenuItem);
    });
  }
}

export default Navigation;
